// Unbatched: the BE validates the date's full order, so a partial payload is rejected.
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'
import { buildSlotSequencePairs } from '../slotPayloads'

import type { SlotRow } from '../slotPayloads'
import type { StepResult } from '../types'
import type { SlotSequence } from 'types/api/route/routeSlotRequests'
import type { RouteId, ServiceDate } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

export interface SequenceDailySlotStepArgs {
  readonly routeId: RouteId
  readonly serviceDate: ServiceDate
  readonly orderedRows: ReadonlyArray<SlotRow>
}

export type SequenceDailySlotStepValue = {
  readonly committedSequence: ReadonlyArray<SlotSequence>
}

export type SequenceDailySlotStepResult = StepResult<'RESEQUENCE_DAILY', SequenceDailySlotStepValue>

export const makeSequenceDailySlotStep = (
  args: SequenceDailySlotStepArgs,
  deps: Deps
): PipelineStep<'RESEQUENCE_DAILY', SequenceDailySlotStepResult> => {
  const stepId = `sequence-daily-slot:${args.routeId}:${args.serviceDate}`
  return {
    id: stepId,
    phase: 'RESEQUENCE_DAILY',
    label: 'Failed to resequence the daily schedule',
    applyOrder: 200,
    run: async ctx => {
      if (!deps.sequenceDailySlot) {
        return err({ kind: 'PRECONDITION_FAILED', reason: 'sequenceDailySlot dep not provided — the v3-slot strategy requires it' })
      }
      const { pairs, droppedDuplicates, droppedMissing } = buildSlotSequencePairs(args.orderedRows)
      if (droppedDuplicates.length > 0 || droppedMissing > 0) {
        ctx.logger.warn(`${stepId}: dropped ${droppedDuplicates.length} duplicate and ${droppedMissing} slot-less rows`)
      }
      if (pairs.length === 0) {
        return ok({ phase: 'RESEQUENCE_DAILY', stepId, value: { committedSequence: [] } })
      }

      try {
        const response = await deps
          .sequenceDailySlot({
            routeId: args.routeId,
            data: { serviceDate: args.serviceDate, sequence: [...pairs] },
            skipInvalidate: true,
          })
          .unwrap()
        return ok({ phase: 'RESEQUENCE_DAILY', stepId, value: { committedSequence: response?.sequence ?? pairs } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'RESEQUENCE_DAILY', status, message })
      }
    },
  }
}
